import { Link } from "react-router-dom";
import { ArrowRight, Palette, Sparkles, BarcodeIcon, PackageCheck, Layers, Beaker } from "lucide-react";
import Reveal from "../components/Reveal";
import PageHeader from "../components/PageHeader";

const SERVICES = [
  { icon: Beaker, title: "OEM Manufacturing", body: "End-to-end contract manufacturing with your specifications, blend formulations and quality parameters." },
  { icon: Sparkles, title: "Private Branding", body: "Your brand identity, our processing discipline — from a single spice line to a full portfolio of retail SKUs." },
  { icon: Layers, title: "Custom Labels", body: "Destination-market compliant labelling — FDA, EU FIC, FSSAI — designed and printed in-house." },
  { icon: PackageCheck, title: "Retail Packaging", body: "Stand-up pouches, PET jars, HDPE containers and printed cartons — tailored to your positioning and retail channel." },
  { icon: PackageCheck, title: "Bulk Packaging", body: "Industrial packaging for foodservice and manufacturing customers with your branded outer cartons." },
  { icon: BarcodeIcon, title: "Barcode & GTIN", body: "GS1 barcode assignment, GTIN registration and destination-market SKU compliance included." },
  { icon: Palette, title: "Packaging Design", body: "In-house creative studio for logo development, dielines, artwork and mockups — from brief to press-ready." },
];

const STEPS = [
  { n: "01", title: "Consult", body: "Share your brand, market and shelf positioning. We map SKUs, MOQs and lead-times." },
  { n: "02", title: "Formulate", body: "Blend development and sample rounds — 5-10kg trial lots refined until you approve the sensory profile." },
  { n: "03", title: "Design", body: "Packaging design, dielines and label copywriting — including regulatory compliance for your target market." },
  { n: "04", title: "Produce", body: "Bulk production with lot-linked QC documentation and Certificate of Analysis per lot." },
  { n: "05", title: "Ship", body: "Container loading, export documentation and destination-port delivery via trusted freight forwarders." },
];

export default function PrivateLabel() {
  return (
    <>
      <PageHeader
        eyebrow="Private Label & OEM"
        title="Build your own spice brand."
        italicWord="own"
        subtitle="End-to-end private label manufacturing for retailers, chefs, direct-to-consumer entrepreneurs and food-service distributors — from blend development to destination-port delivery."
      />

      {/* SERVICES */}
      <section className="py-24 md:py-32 bg-brand-ivory">
        <div className="container-luxe">
          <Reveal className="max-w-xl mb-16">
            <span className="overline">Full-service manufacturing</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight text-brand-charcoal">What we handle for you.</h2>
            <span className="divider-gold" />
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 60} className="border-l border-brand-gold/30 pl-6 py-2">
                <div className="w-11 h-11 border border-brand-green/50 flex items-center justify-center text-brand-green mb-5">
                  <s.icon className="w-5 h-5" />
                </div>
                <h4 className="font-serif text-2xl text-brand-charcoal mb-2">{s.title}</h4>
                <p className="text-brand-charcoal/60 text-[14.5px] leading-relaxed">{s.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 md:py-32 bg-brand-ink text-brand-ivory relative overflow-hidden">
        <div className="container-luxe grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-4">
            <span className="overline">The Process</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05]">Five steps to your first shipment.</h2>
            <span className="divider-gold" />
            <p className="text-brand-ivory/60 leading-relaxed">A typical private label engagement moves from first consultation to first container in 8-12 weeks — including sample rounds, packaging design and production.</p>
          </Reveal>
          <div className="lg:col-span-7 lg:col-start-6 space-y-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 80} className="flex gap-8 items-start border-b border-brand-ivory/10 pb-8 last:border-0">
                <div className="font-serif text-5xl text-brand-gold/70 leading-none">{s.n}</div>
                <div>
                  <h4 className="font-serif text-2xl text-brand-ivory mb-2">{s.title}</h4>
                  <p className="text-brand-ivory/60 text-[15px] leading-relaxed max-w-xl">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand-ivory-warm text-center">
        <div className="container-luxe max-w-2xl mx-auto">
          <h3 className="font-serif text-4xl text-brand-charcoal">Ready to build your line?</h3>
          <p className="mt-6 text-brand-charcoal/65 leading-relaxed">MOQs start at 200kg per SKU. Sample lots of 5-10kg can be produced for tasting panels before you commit to a full run.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/request-quote" className="luxe-btn-primary" data-testid="privatelabel-cta">Start a Private Label Enquiry <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
